"use client";
import type { Media, MobileHighlightsBlock } from "@mw/shared/payload-types";

import { Block } from "@/app/_blocks/Block";
import { getVideoUrl } from "@/app/_utilities/payload";
import { Heading } from "@components/core/Heading";
import { Image } from "@components/core/Image";
import { IphoneMockup } from "@components/core/IphoneMockup";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { AnimatePresence, cubicBezier } from "motion/react";
import * as m from "motion/react-m";
import { useEffect, useRef, useState } from "react";

type BlockMobileHighlightsProp = Pick<MobileHighlightsBlock, "highlights">;

export const BlockMobileHighlights = ({ highlights }: BlockMobileHighlightsProp) => {
  const benefitsRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const isInView = useInView(benefitsRef, { once: true });
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (isInView) {
      benefitsRef.current?.querySelector("video")?.play();
    }
  }, [isInView]);

  useEffect(() => {
    if (isInView) {
      benefitsRef.current?.querySelector("video")?.play();
    }
  }, [isInView]);

  if (!highlights) {
    return null;
  }

  return (
    <Block className='container space-y-40' name='languages-section'>
      <div className='flex flex-col items-center' ref={benefitsRef}>
        <div className='grid-cols-1fr inter grid h-full max-w-[1112px] gap-16 md:grid-cols-[3fr_3fr] md:gap-16 md:p-0 lg:gap-40 lg:p-16'>
          <ul className='item-stretch relative flex flex-col justify-center gap-16 md:gap-20'>
            {highlights.map((highlight, index) => {
              return (
                <div key={index}>
                  <m.button
                    className={cn(
                      cn(
                        "effect-focus relative overflow-hidden rounded-xl border bg-gradient-to-b p-1 text-left transition-all duration-200",
                        activeIndex === index && "border-darkest",
                      ),
                    )}
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    ref={el => {
                      buttonRefs.current[index] = el;
                    }}
                  >
                    <div className='relative flex flex-col gap-1 rounded-xl bg-[#fff] px-20 py-20 lg:px-28 lg:py-24'>
                      <Heading className={cn("f-subhead-02")} level={2}>
                        {highlight.title}
                      </Heading>
                      <p className='f-description-03 mt-4'>{highlight.description}</p>
                    </div>
                  </m.button>
                  <m.div
                    animate={{
                      height: activeIndex === index ? "auto" : 0,
                    }}
                    className='mt-8 flex h-0 justify-center overflow-hidden md:hidden'
                    transition={{
                      duration: 0.4,
                    }}
                  >
                    <m.div
                      animate={{
                        opacity: activeIndex === index ? 1 : 0,
                        scale: activeIndex === index ? 1 : 0.98,
                      }}
                      transition={{
                        duration: 0.3,
                        delay: activeIndex === index ? 0.3 : 0,
                      }}
                    >
                      <IphoneMockup>
                        <div className='absolute left-0 right-0 top-0 z-10' key={index}>
                          {(highlight.video as Media)?.url ? (
                            <video
                              autoPlay
                              className='max-w-full'
                              controls={false}
                              height={661}
                              loop
                              playsInline
                              src={getVideoUrl(highlight.video as Media)}
                              width={326}
                            />
                          ) : null}
                          {((highlight.image as Media)?.url as string) ? (
                            <Image
                              alt={highlight.title as string}
                              className='max-w-full'
                              height={661}
                              resource={highlight.image}
                              width={326}
                            />
                          ) : null}
                        </div>
                      </IphoneMockup>
                    </m.div>
                  </m.div>
                </div>
              );
            })}
          </ul>
          <div className='relative z-1 hidden items-center justify-center md:flex'>
            <IphoneMockup>
              <AnimatePresence>
                {highlights.map((highlight, index) => {
                  const isActive = index === activeIndex;

                  if (!isActive) {
                    return null;
                  }
                  return (
                    <m.div
                      animate={{ opacity: 1 }}
                      className='absolute left-0 right-0 top-0 z-10'
                      exit={{ opacity: 1 }}
                      initial={{ opacity: 0 }}
                      key={index}
                      transition={{
                        ease: cubicBezier(0.7, 0.05, 0.4, 1),
                        duration: 0.5,
                      }}
                    >
                      {((highlight.video as Media)?.url as string) ? (
                        <video
                          autoPlay
                          className='max-w-full'
                          controls={false}
                          height={661}
                          loop
                          muted
                          playsInline
                          src={getVideoUrl(highlight.video as Media)}
                          width={326}
                        />
                      ) : null}
                      {((highlight.image as Media)?.url as string) ? (
                        <Image
                          alt={highlight.title as string}
                          className='max-w-full'
                          height={661}
                          resource={highlight.image}
                          width={326}
                        />
                      ) : null}
                    </m.div>
                  );
                })}
              </AnimatePresence>
            </IphoneMockup>
          </div>
        </div>
      </div>
    </Block>
  );
};
